import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { AttachFile as AttachFileIcon } from "@mui/icons-material";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import AnnotationMentionTextarea from "./AnnotationMentionTextarea";
import Loader from "../../../components/Loaders/Loader";
import AttachmentChip from "../../../components/Modal/ModalImageEdit/shared/AttachmentChip";
import AttachmentsScrollRow from "../../../components/Modal/ModalImageEdit/shared/AttachmentsScrollRow";
import { ATTACHMENT_CHIP_VARIANTS } from "../../../components/Modal/ModalImageEdit/shared/attachmentChipTheme";
import { useTranslationApp } from "../../../lib/i18next";
import { useSelectorApp } from "../../../lib/redux";
import {
  ANNOTATIONS_MENTION_ALL,
  ANNOTATIONS_MESSAGE_CANCEL_EDIT,
  ANNOTATIONS_SIDEBAR_INPUT_PLACEHOLDER,
  BUTTON_SEND,
} from "../../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import UploadFileButton from "../../../components/Buttons/UploadFileButton";
import {
  FILE_FORMATS,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../../utils/constants";
import {
  normalizeAnnotationTextForEdit,
  resolveMentionedUserIDs,
} from "../utils/annotationMentions";
import {
  convertMentionsMarkupToStorage,
  convertStorageToMentionsMarkup,
  getMentionsPlainText,
} from "../utils/mentionInputAdapters";

const ANNOTATION_ACCEPTED_FORMATS = [
  ...IMAGE_FORMATS,
  ...FILE_FORMATS,
  ...VIDEO_FORMATS,
];

const resetInputState = ({
  setValue,
  setAttachmentsFiles,
  setFileNamesMapping,
  setPendingFilesCount,
  editorRef = null,
}) => {
  setValue("annotationInput", "", {
    shouldDirty: true,
    shouldTouch: true,
  });
  editorRef?.current?.clear?.();
  setAttachmentsFiles([]);
  setFileNamesMapping({});
  setPendingFilesCount(0);
  window.pendingAnnotationFiles = [];
};

const AnnotationsChatInput = ({
  onSend,
  onCancelEdit = () => {},
  isProcessing = false,
  brandName = "",
  ownersByID = {},
  editingAnnotation = null,
}) => {
  const { t } = useTranslationApp();
  const ownerUserID = useSelectorApp((state) => state.auth.user?.uid);
  const inputRef = useRef(null);
  const editorRef = useRef(null);
  const wasEditingRef = useRef(false);
  const { watch, setValue } = useForm({
    defaultValues: {
      annotationInput: "",
    },
  });

  const annotationInput = watch("annotationInput");
  const [attachmentsFiles, setAttachmentsFiles] = useState([]);
  const [fileNamesMapping, setFileNamesMapping] = useState({});
  const [pendingFilesCount, setPendingFilesCount] = useState(0);

  const isEditing = Boolean(editingAnnotation?.annotationID);
  const plainInputText = getMentionsPlainText(annotationInput || "");
  const isSendDisabled = !plainInputText.trim() || isProcessing;

  const onTextareaChange = (_event, nextValue) => {
    setValue("annotationInput", nextValue);
  };

  const onFileSelect = (files) => {
    window.pendingAnnotationFiles = Array.from(files);
    setPendingFilesCount(files.length);
  };

  const onFileUpload = (newUrls) => {
    const urlsArray = Array.isArray(newUrls) ? newUrls : [newUrls];

    if (
      window.pendingAnnotationFiles &&
      window.pendingAnnotationFiles.length > 0
    ) {
      const newMapping = {};

      urlsArray.forEach((url) => {
        if (window.pendingAnnotationFiles.length > 0) {
          const file = window.pendingAnnotationFiles.shift();
          newMapping[url] = file.name;
        }
      });

      setFileNamesMapping((prev) => ({ ...prev, ...newMapping }));
    }

    setPendingFilesCount((prev) => Math.max(0, prev - urlsArray.length));
    setAttachmentsFiles((prevFiles) => [...prevFiles, ...urlsArray]);
  };

  const onRemoveAttachment = (index) => {
    const removedFileUrl = attachmentsFiles[index];
    setFileNamesMapping((prev) => {
      const newMapping = { ...prev };
      delete newMapping[removedFileUrl];
      return newMapping;
    });
    setAttachmentsFiles((prevFiles) =>
      prevFiles.filter((_, fileIndex) => fileIndex !== index)
    );
  };

  const onSubmit = () => {
    if (isSendDisabled) return;

    const storageText = convertMentionsMarkupToStorage(annotationInput.trim());

    const files = attachmentsFiles.map((url) => ({
      url,
      name: fileNamesMapping[url] || url.split("/").pop() || "file",
    }));

    const mentionedUserIDs = resolveMentionedUserIDs({
      text: storageText,
      ownersByID,
      t,
      allLabelKey: ANNOTATIONS_MENTION_ALL,
      excludeUserID: ownerUserID,
    });

    onSend({
      text: storageText,
      files,
      mentionedUserIDs,
      annotationID: editingAnnotation?.annotationID ?? null,
    });

    if (!isEditing) {
      resetInputState({
        setValue,
        setAttachmentsFiles,
        setFileNamesMapping,
        setPendingFilesCount,
        editorRef,
      });
    }
  };

  const onCancelEditing = () => {
    wasEditingRef.current = false;
    resetInputState({
      setValue,
      setAttachmentsFiles,
      setFileNamesMapping,
      setPendingFilesCount,
      editorRef,
    });
    onCancelEdit();
  };

  useEffect(() => {
    if (editingAnnotation?.annotationID) {
      wasEditingRef.current = true;

      const nextFiles = Array.isArray(editingAnnotation.files)
        ? editingAnnotation.files
        : [];
      const nextMapping = {};

      nextFiles.forEach((file) => {
        if (file?.url) {
          nextMapping[file.url] =
            file.name || file.url.split("/").pop() || "file";
        }
      });

      const normalizedText = normalizeAnnotationTextForEdit({
        text: editingAnnotation.text || "",
        ownersByID,
        t,
        allLabelKey: ANNOTATIONS_MENTION_ALL,
      });

      setValue(
        "annotationInput",
        convertStorageToMentionsMarkup({
          text: normalizedText,
          ownersByID,
          t,
          allLabelKey: ANNOTATIONS_MENTION_ALL,
        })
      );
      setAttachmentsFiles(nextFiles.map((file) => file.url).filter(Boolean));
      setFileNamesMapping(nextMapping);
      setPendingFilesCount(0);
      window.pendingAnnotationFiles = [];
      return;
    }

    if (wasEditingRef.current) {
      wasEditingRef.current = false;
      resetInputState({
        setValue,
        setAttachmentsFiles,
        setFileNamesMapping,
        setPendingFilesCount,
        editorRef,
      });
    }
  }, [
    editingAnnotation?.annotationID,
    editingAnnotation,
    ownersByID,
    setValue,
    t,
  ]);

  return (
    <Box
      sx={{
        px: 1.5,
        pt: 0.2,
        pb: 1.5,
        backgroundColor: "background.paper",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 0.4,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: 3,
          pt: 1.5,
          pb: 1,
          px: 1,
        }}
      >
        {isEditing ? (
          <IconButton
            aria-label={t(ANNOTATIONS_MESSAGE_CANCEL_EDIT)}
            onClick={onCancelEditing}
            size="small"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              zIndex: 2,
              p: 0.25,
              color: "text.secondary",
            }}
          >
            <X size={16} />
          </IconButton>
        ) : null}

        {(attachmentsFiles.length > 0 || pendingFilesCount > 0) && (
          <AttachmentsScrollRow>
            {attachmentsFiles.map((url, idx) => (
              <AttachmentChip
                key={`${url}-${idx}`}
                url={url}
                fileName={fileNamesMapping[url] || `file-${idx + 1}`}
                variant={ATTACHMENT_CHIP_VARIANTS.LIGHT}
                onRemove={() => onRemoveAttachment(idx)}
              />
            ))}
            {Array.from({ length: pendingFilesCount }).map((_, index) => (
              <Box
                key={`skeleton-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  backgroundColor: "action.hover",
                  maxWidth: 240,
                  minWidth: 120,
                  flexShrink: 0,
                }}
              >
                <Skeleton variant="rectangular" width={22} height={22} />
                <Skeleton
                  variant="text"
                  width={80}
                  height={16}
                  sx={{ flex: 1 }}
                />
                <Skeleton variant="circular" width={16} height={16} />
              </Box>
            ))}
          </AttachmentsScrollRow>
        )}

        <AnnotationMentionTextarea
          value={annotationInput || ""}
          onChange={onTextareaChange}
          onSubmit={onSubmit}
          placeholder={t(ANNOTATIONS_SIDEBAR_INPUT_PLACEHOLDER, {
            name: brandName || "",
          })}
          disabled={isProcessing}
          ownersByID={ownersByID}
          excludeUserID={ownerUserID}
          inputRef={inputRef}
          editorRef={editorRef}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <UploadFileButton
            disableVerificationMedia={true}
            id="upload-annotations-files"
            value={false}
            name="annotationAttachments"
            acceptedFormats={ANNOTATION_ACCEPTED_FORMATS}
            multiple={true}
            pathStorage={`annotations/${ownerUserID}/`}
            onChange={onFileUpload}
            onFileSelect={onFileSelect}
            type="icon"
            icon={<AttachFileIcon sx={{ fontSize: "20px" }} />}
            sx={{
              minWidth: "40px !important",
              width: "40px !important",
              height: "40px !important",
              p: 0.5,
              borderRadius: 1,
            }}
          />

          {isProcessing ? (
            <Loader hasMarginTop={false} size={18} />
          ) : (
            <Typography
              component="button"
              type="button"
              variant="body2"
              onClick={onSubmit}
              disabled={isSendDisabled}
              sx={{
                fontWeight: 700,
                border: "none",
                background: "none",
                px: 1,
                cursor: isSendDisabled ? "default" : "pointer",
                color: isSendDisabled ? "text.disabled" : "text.primary",
              }}
            >
              {t(BUTTON_SEND)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AnnotationsChatInput;
